/**
 * Copilot agent <-> client contract.
 *
 * An assistant turn is an ordered list of content BLOCKS streamed over
 * AGENT_EVENTS. The same event objects flow over the in-memory mock transport
 * today and named SSE events later (mirror of the DeepResearch
 * researchStream/researchProgress pattern) — every event carries a monotonic
 * `ts` so a polling fallback can replay with `?since=cursor`.
 *
 * Client -> agent communication is CLIENT_EVENTS; structured interactions
 * (quick replies, CTA clicks, confirmations, OAuth results) reference the
 * block that requested them via `blockId`.
 */

export const BLOCK = {
  TEXT: "text", // { markdown } — filled incrementally via block_delta
  QUICK_REPLIES: "quick_replies", // { prompt, replyId, options: [{value,label,description?}], selectedValue? }
  CTA_CHIPS: "cta_chips", // { chips: [{actionId,label,icon?,style?,tableId?,path?}], clickedActionId? }
  TABLE_REF: "table_ref", // { tableId, title, totalCount, columns: [{key,label,width?}] }
  // Saved contacts as a compact Contacts page inline in the thread: same rows,
  // real pagination, row selection, bulk + per-row actions. Shares TABLE_REF's
  // tableId paging. { tableId, title, totalCount, columns, selectable, search }
  PROGRESS: "progress", // { status: running|done|failed, label, messages: [], percent?, detail? }
  CONFIRMATION: "confirmation", // { confirmId, prompt, confirmLabel, cancelLabel, resolution?, destructive? }
  CONNECT_INTEGRATION: "connect_integration", // { provider, actionId, label, description, status: pending|connected|failed }
  SUMMARY_CARD: "summary_card", // { title, rows: [{label,value,tone?}], footnote? }
  ERROR_CTA: "error_cta", // { title, markdown, severity: warn|error, chips }
  // { previewId, kind: "sequence_builder", title, status: building|done|failed,
  //   steps: [{type, subject?, body?, linkedin_action?, message?, days_later}],
  //   sequenceId?, autoOpen? } — view-only side panel showing the agent's
  //   mutation happening in realtime; patched via block_update.
  LIVE_PREVIEW: "live_preview",
  // { questions: [{question, header?, multiSelect?, options: [{label, description?}]}],
  //   done?, answers?, skipped? } — structured requirement-gathering: the
  //   agent asks BEFORE generating; answered via QUESTION_FORM_RESPONSE.
  //   Single-select answers instantly on click; multiSelect lets the user tick
  //   several options then submit. Previous navigates back across questions.
  QUESTION_FORM: "question_form",
  // { kind, title, items: [{id, title, subtitle?, badges?: [string],
  //   fields: [{label, value}], detail: {...}}] } — list-shaped tool results
  //   rendered as cards; clicking one opens its full `detail` in the canvas.
  ENTITY_CARDS: "entity_cards",
  // Sequences as the product's own list rows — activate toggle, name, contacts,
  // open/click/reply rates, created. { sequences: [{id,name,status,contacts,
  // openRate,clickRate,replyRate,created,shareType,folderName}],
  //   total?, loadedCount?, loading?, loadError? } — total is the count across
  //   ALL pages for this list's filters, so the "Load N more" CTA can count
  //   down accurately; absent on lists with no page behind them.
  SEQUENCE_LIST: "sequence_list",
  // { name, steps: [{type, subject?, body?, message?, linkedin_action?,
  //   automated?, notes?, days_later}], status?, clickedActionId? } —
  //   pre-create draft review: builder-style step cards + Create CTA
  DRAFT_REVIEW: "draft_review",
  // { profile: {...raw draft fields from enrich_seller_profile}, done?, saved? }
  //   — in-chat, editable seller-profile draft (SG-2182): per-section cards
  //   with inline field edits and pill add/remove for list fields. Approving
  //   emits SELLER_PROFILE_APPROVED with the (possibly edited) profile.
  SELLER_PROFILE_PREVIEW: "seller_profile_preview",
  // { done? } — dedicated text-box + "Research" button for collecting a
  // company website (SG-2182). Replaces asking for the website via
  // QUESTION_FORM's free-text option, which read as a generic answered
  // checklist item instead of a clear input; submitting fires
  // WEBSITE_SUBMITTED and triggers real research directly, bypassing the
  // model for this one action.
  WEBSITE_INPUT: "website_input",
  // { kind, title, tiles: [{label, value, unit?}],
  //   distribution: [{stage, stageLabel, count}] } — analytics tool results
  //   drawn with the dashboard's own ContactStagesWidget at chat width, so the
  //   in-chat chart matches the reports page (including the viz toggle).
  //   Emitted by salesgear-mcp orchestrator/charts.ts.
  CHART: "chart",
  // { kind, items: [ONE item, same shape as entity_cards] } — one record from
  //   a get_* tool, shown inline collapsed to a few fields. Reuses the entity
  //   side panel for "Open full record". Emitted from RECORD_TOOLS.
  RECORD_DETAIL: "record_detail",
  // { kind, title, events: [{at, atLabel, type, title, detail}] } — activity
  //   tools as a dated rail, newest first. Emitted from TIMELINE_TOOLS.
  TIMELINE: "timeline",
  // { citations: [{url, title, domain}] } — provenance for research answers.
  CITATIONS: "citations",
  // { title, messages: [{from, at, subject, body, outbound}] } — reply thread
  //   rendered as a conversation. Both emitted from sources.ts.
  THREAD_VIEW: "thread_view",
  // { label, value, unit?, decimals?, footnote?, sparkline?: [number],
  //   delta?: { value, direction: up|down|flat, sentiment: positive|negative|
  //   neutral, unit?, label? } } — one headline number. `direction` picks the
  //   arrow, `sentiment` picks the colour: a rising bounce rate is up AND bad.
  STAT_TILE: "stat_tile",
  // { title?, tiles: [ …stat_tile fields ] } — 2–4 headline numbers. Past 4
  //   the row stops reading as headlines; send the rest as a data_table.
  KPI_ROW: "kpi_row",
  // { title?, kind, value, unit?, threshold?: { value, unit?, label?, inverse? } }
  //   — a ratio against a bounded maximum. `threshold` should come from a tool;
  //   visualConfig holds the fallback. No threshold ⇒ the block refuses to draw.
  METER: "meter",
  // { title?, stages: [{label, value}] } — step-to-step conversion is primary,
  //   cumulative-from-original secondary. Highlights the worst single drop.
  FUNNEL: "funnel",
  // { title?, unit?, rows: [{label, values: [{label, value}]}] } — 2-D grid,
  //   sequential ramp only. Refuses to draw below ~20 cells (use a table).
  HEATMAP: "heatmap",
  // { title?, unit?, fromLabel?, toLabel?, rows: [{label, from, to}] } —
  //   before/after per row; clearer than a grouped bar for exactly two points.
  DUMBBELL: "dumbbell",
  // { title?, options: [{title, subtitle?, value?, unit?, points?: [string],
  //   recommended?, prompt?, ctaLabel?}] } — option cards, max 4. Feature
  //   matrices belong in a markdown table in the message, not in the cards.
  COMPARISON_GRID: "comparison_grid",
  // { title?, unit?, seriesName?, rows: [{label, value}] } — values either
  //   side of zero, neutral grey midpoint.
  DIVERGING_BAR: "diverging_bar",
  // { title?, tableId?, columns: [{key,label,numeric?,unit?}], rows? } —
  //   inline table. Pages via the existing tableId machinery; sorting is
  //   client-side over the current page and says so.
  DATA_TABLE: "data_table",
  // { title?, stages: [{title, body?, meta?}] } — one panel per stage, Next
  //   wraps last→first. This is how cycles are drawn, never as a ring.
  STEPPER: "stepper",
  // { title?, svg } — model-authored diagram rendered as INLINE SVG (no
  //   iframe) after passing components/visuals/sanitizeSvg.js, which is a
  //   strict allowlist and the security boundary. `svg` fills via block_delta;
  //   nothing is sanitized or mounted until streaming ends, because partial
  //   SVG is not parseable. A <g data-prompt="..."> is clickable and sends its
  //   value as a user turn.
  DIAGRAM: "diagram",
  // { title?, html } — arbitrary HTML+JS in an iframe with
  //   sandbox="allow-scripts" and NO allow-same-origin, so the frame has an
  //   opaque origin and cannot reach our cookies, storage or DOM. Last resort;
  //   see docs/DECISIONS.md for the inherited-CSP gap.
  WIDGET: "widget",
  // { title?, unit?, rows: [{label, value}], highlight?, preserveOrder?,
  //   horizontal? } — magnitude across categories with EMPHASIS colouring:
  //   one bar accented, the rest grey. `highlight` names a row; omitted means
  //   the largest; null opts out. Sorted descending unless preserveOrder.
  BAR_CHART: "bar_chart",
  // { title?, unit?, categories: [string], series: [{name, data: [number]}] }
  //   — trend over time. Each series gets a dash pattern AND marker shape as a
  //   second cue beyond colour, named in the legend. Single y-axis only.
  LINE_CHART: "line_chart"
};

export const AGENT_EVENTS = {
  TURN_START: "turn_start", // { turnId }
  BLOCK_START: "block_start", // { turnId, block }
  BLOCK_DELTA: "block_delta", // { turnId, blockId, delta } — append to text markdown
  BLOCK_UPDATE: "block_update", // { turnId, blockId, patch } — shallow merge
  BLOCK_END: "block_end", // { turnId, blockId }
  TURN_END: "turn_end", // { turnId, await: user_input|quick_reply|cta|confirmation|action_result|question_form|null, hint? }
  AGENT_ERROR: "agent_error", // { turnId?, message }
  // { items: [{kind: "reply"|"action", label, actionId?, tableId?, path?, icon?}] }
  // — chips for the next user turn: reply chips send text, action chips drive
  // the UI (open table, navigate). Plain strings normalize to reply chips.
  SUGGESTIONS: "suggestions"
};

export const CLIENT_EVENTS = {
  USER_MESSAGE: "user_message", // { text }
  QUICK_REPLY_SELECTED: "quick_reply_selected", // { blockId, replyId, value }
  CTA_CLICKED: "cta_clicked", // { blockId, actionId }
  CONFIRMATION_RESPONSE: "confirmation_response", // { blockId, confirmId, confirmed }
  ACTION_RESULT: "action_result", // { blockId, actionId, status: success|failed|dismissed, data? }
  QUESTION_FORM_RESPONSE: "question_form_response", // { blockId, answers: [{question, answer|null}] }
  SELLER_PROFILE_APPROVED: "seller_profile_approved", // { blockId, profile: {...edited fields} }
  VIEW_SEQUENCE: "view_sequence", // { sequenceId, name } -> agent emits a live_preview the panel opens
  LOAD_MORE_SEQUENCES: "load_more_sequences", // { blockId } -> agent appends the next page to that sequence_list
  // { blockId, listId, rows } -> orchestrator saves them via
  // /v1/contact-lists/{id}/prospects (the agent's own save call, no model) and
  // patches the block with { listResult: {added, skipped, requested} }.
  ADD_PROSPECTS_TO_LIST: "add_prospects_to_list",
  // { sequenceId } -> orchestrator renders the contact table bound to that
  // sequence, so the bulk bar can enrol straight into it. No model call.
  LIST_CONTACTS_FOR_SEQUENCE: "list_contacts_for_sequence",
  SELLER_PROFILE_DISCARDED: "seller_profile_discarded", // { blockId }
  WEBSITE_SUBMITTED: "website_submitted" // { blockId, website }
};

export const AWAIT = {
  USER_INPUT: "user_input",
  QUICK_REPLY: "quick_reply",
  CTA: "cta",
  CONFIRMATION: "confirmation",
  ACTION_RESULT: "action_result",
  QUESTION_FORM: "question_form",
  SELLER_PROFILE_PREVIEW: "seller_profile_preview",
  WEBSITE_INPUT: "website_input"
};

// Chip action ids the CLIENT resolves locally (in addition to reporting the
// click to the agent). Anything else is agent-side only.
export const LOCAL_ACTIONS = {
  VIEW_TABLE: "view_table", // chip must carry `tableId`
  NAVIGATE: "navigate", // chip must carry `path` (in-app route)
  CONTACT_SUPPORT: "contact_support", // opens Intercom (same as product-wide pattern)
  ADD_CONTACTS: "add_contacts" // chip must carry `sequenceId`
};

// Product-wide support entry point (UpgradePopup, banners, etc. use the same).
export const openSupportChat = () => {
  if (window.Intercom) {
    window.Intercom("show");
  }
};
